import { useRouter } from '@tanstack/react-router'
import { useState, type JSX } from 'react'

import { AccountMenu } from './account-menu.tsx'
import { Icon, type IconName } from '../design-system/icon.tsx'
import { Link } from '../design-system/link.tsx'
import { cn } from '../lib/cn.ts'
import { isApplePlatform, searchShortcutLabel } from '../lib/page-search.ts'
import {
  normalizePath,
  OPERATIONS_GROUP,
  sectionVisibility,
  visibleNavSections,
  type InternalNavSection,
  type InternalPage
} from '../lib/pages.ts'

/** The quiet control style on the navy rail: the menu button. */
export const SIDEBAR_CONTROL_CLASS =
  'text-sidebar-foreground hover:bg-sidebar-accent/40 hover:text-sidebar-foreground focus-visible:ring-sidebar-ring'

function rowClass(active: boolean): string {
  return cn(
    'text-sidebar-foreground/80 flex items-center gap-3 rounded-md px-3 py-2 text-sm',
    'hover:bg-sidebar-accent/40 focus-visible:ring-sidebar-ring cursor-pointer',
    'text-left focus-visible:ring-2 focus-visible:outline-none',
    active && 'bg-sidebar-accent/60 text-sidebar-accent-foreground font-medium'
  )
}

/** A group name as a `data-testid` fragment: lower-cased, spaces to hyphens. "CS Tools" becomes "cs-tools". */
function slugifyGroup(group: string): string {
  return group.trim().toLowerCase().replace(/\s+/g, '-')
}

/** Every page in this console renders the same row; a group header and a flat entry both build one from it. */
function NavRow({
  page,
  active,
  testId,
  onNavigate,
  icon = 'app-window'
}: {
  page: InternalPage
  active: boolean
  testId: string
  onNavigate: () => void
  icon?: IconName
}): JSX.Element {
  return (
    <Link
      data-testid={testId}
      href={page.path}
      onNavigate={onNavigate}
      className={rowClass(active)}
      aria-current={active ? 'page' : undefined}
    >
      <Icon name={icon} className="size-4 shrink-0" />
      {page.title}
    </Link>
  )
}

/**
 * One page in customize mode: a switch that shows or hides it in the rail.
 *
 * A switch rather than a link, because in this mode a press chooses and does
 * not navigate. Its accessible name is the page title and its state is
 * whether the rail shows it, which is the question the reader is answering.
 */
function PageSwitch({
  page,
  shown,
  testId,
  onToggle
}: {
  page: InternalPage
  shown: boolean
  testId: string
  onToggle: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={shown}
      data-testid={testId}
      className={cn(
        rowClass(false),
        'w-full justify-between',
        !shown && 'text-sidebar-foreground/60'
      )}
      onClick={onToggle}
    >
      <span className="flex items-center gap-3">
        <Icon name="app-window" className="size-4 shrink-0" />
        {page.title}
      </span>
      <Icon name={shown ? 'eye' : 'eye-off'} className="size-4 shrink-0" />
    </button>
  )
}

/**
 * One section's header in customize mode: a checkbox that hides or shows the
 * whole section.
 *
 * It is a checkbox and not a switch, because a section can be part shown, and
 * only a checkbox has a mixed state. A press hides every page while any is
 * shown, and shows every page once none is.
 */
function SectionCheckbox({
  section,
  hiddenPages,
  testId,
  onToggle
}: {
  section: InternalNavSection
  hiddenPages: readonly string[]
  testId: string
  onToggle: () => void
}): JSX.Element {
  const { shown, total } = sectionVisibility(section, hiddenPages)
  const count = `${shown} of ${total} shown`

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={shown === total ? true : shown === 0 ? false : 'mixed'}
      aria-label={`${section.group}, ${count}`}
      data-testid={testId}
      className={cn(
        rowClass(false),
        'text-sidebar-foreground/70 min-w-0 flex-1 justify-between text-xs font-medium'
      )}
      onClick={onToggle}
    >
      {section.group}
      <span className="flex items-center gap-2 font-normal">
        {count}
        <Icon
          name={shown > 0 ? 'eye' : 'eye-off'}
          className="size-4 shrink-0"
        />
      </span>
    </button>
  )
}

/** What `AppNav` needs from the layout that owns the rail's state. */
export interface AppNavProps {
  pathname: string
  mobile?: boolean
  onSelect: () => void
  /** Every declared section, before any page is hidden. */
  sections: readonly InternalNavSection[]
  /** The groups the reader has closed. A group absent from this list is open. */
  collapsedGroups: readonly string[]
  onToggleGroup: (group: string) => void
  /** The page ids the reader hid. A page absent from this list is shown. */
  hiddenPages: readonly string[]
  onTogglePage: (pageId: string) => void
  /** Hide every page of a section that shows any, or show every page of one that shows none. */
  onToggleSection: (section: InternalNavSection) => void
  onShowAllPages: () => void
  onMoveSection: (group: string, direction: -1 | 1) => void
  /** Whether the rail lists every page as a switch, to choose what it shows. */
  customizing: boolean
  onCustomizingChange: (customizing: boolean) => void
  /** Why the last rail change was not saved, or null. */
  saveError: string | null
  accessStatus: 'loading' | 'error' | 'ready'
  /** Opens page search over the console. */
  onOpenSearch: () => void
}

/**
 * The page list, in desktop and mobile layouts alike.
 *
 * **Hiding a page is a display choice, not access control.** A hidden page is
 * still reachable by its link, and the rail still shows it while it is the
 * current page, so a deep link never lands where the rail cannot name it.
 *
 * @param props - Nav props; see `AppNavProps`.
 * @returns The rendered page list.
 */
export function AppNav({
  pathname,
  mobile = false,
  onSelect,
  sections,
  collapsedGroups,
  onToggleGroup,
  hiddenPages,
  onTogglePage,
  onToggleSection,
  onShowAllPages,
  onMoveSection,
  customizing,
  onCustomizingChange,
  saveError,
  accessStatus,
  onOpenSearch
}: AppNavProps): JSX.Element {
  const router = useRouter()
  const [moveNotice, setMoveNotice] = useState('')
  const prefix = mobile ? 'mobile-' : ''
  const activePath = normalizePath(pathname)
  const shownSections =
    accessStatus !== 'ready'
      ? []
      : customizing
        ? sections
        : visibleNavSections(sections, hiddenPages, activePath)
  // The shown sections may have lost pages, so a hidden count reads the
  // declared section with the same name.
  const declared = new Map(sections.map(section => [section.group, section]))

  function navigate(path: string): void {
    onSelect()
    router.history.push(path)
  }

  // `min-h-0` and `overflow-y-auto` sit on the page list only. The title, the
  // line under it and Home stay outside that list, so a long list cannot
  // scroll any of them away.
  return (
    <nav
      data-testid={`${prefix}nav-landmark`}
      aria-label="Internal apps"
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="shrink-0 px-3 pt-3">
        {/* In the sheet, `pr-8` keeps the customize control clear of the
            sheet's own close control, which sits at the same top right. */}
        <div
          className={cn(
            'flex items-center justify-between gap-2 pt-2',
            mobile && 'pr-8'
          )}
        >
          <p
            data-testid={`${prefix}app-title`}
            className="text-sidebar-foreground px-3 text-lg font-semibold whitespace-nowrap"
          >
            BlytzPay Internal
          </p>
          {/* One element in both modes, so focus stays on it when it flips.
              Small in both, so the title beside it never wraps. */}
          <button
            type="button"
            data-testid={`${prefix}nav-customize`}
            aria-label={customizing ? undefined : 'Customize pages'}
            disabled={accessStatus !== 'ready'}
            className={cn(
              rowClass(false),
              'shrink-0',
              customizing ? 'px-2 py-1 text-xs font-medium' : 'p-2'
            )}
            onClick={() => onCustomizingChange(!customizing)}
          >
            {customizing ? (
              'Done'
            ) : (
              <Icon name="sliders-horizontal" className="size-4" />
            )}
          </button>
        </div>
        <div
          data-testid={`${prefix}nav-divider`}
          className="bg-sidebar-border my-2 h-px"
        />
        {accessStatus === 'ready' ? (
          <>
            <button
              type="button"
              data-testid={`${prefix}nav-search`}
              className={cn(rowClass(false), 'mb-1 w-full')}
              onClick={onOpenSearch}
              aria-keyshortcuts={isApplePlatform() ? 'Meta+K' : 'Control+K'}
            >
              <Icon name="search" className="size-4 shrink-0" />
              <span className="flex-1">Search</span>
              <span
                aria-hidden="true"
                className="text-sidebar-foreground/60 text-xs"
              >
                {searchShortcutLabel(isApplePlatform())}
              </span>
            </button>
            <Link
              data-testid={`${prefix}nav-home`}
              href="/"
              onNavigate={() => navigate('/')}
              className={cn(rowClass(activePath === '/'), 'mb-1')}
              aria-current={activePath === '/' ? 'page' : undefined}
            >
              <Icon name="home" className="size-4 shrink-0" />
              Home
            </Link>
          </>
        ) : null}
        {customizing ? (
          <p
            data-testid={`${prefix}nav-customize-hint`}
            className="text-sidebar-foreground/70 px-3 pb-2 text-xs"
          >
            Choose pages and move sections. A hidden page still opens from its
            link.
          </p>
        ) : null}
        {saveError ? (
          <p
            role="status"
            data-testid={`${prefix}nav-save-error`}
            className="text-sidebar-foreground/70 px-3 pb-2 text-xs"
          >
            {saveError}
          </p>
        ) : null}
        <p
          role="status"
          data-testid={`${prefix}nav-move-status`}
          className="sr-only"
        >
          {moveNotice}
        </p>
      </div>
      <div
        data-testid={`${prefix}nav-pages`}
        className="scrollbar-sidebar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3"
      >
        {accessStatus !== 'ready' ? (
          <p
            data-testid={`${prefix}nav-access-${accessStatus}`}
            className="text-sidebar-foreground/70 px-3 py-2 text-sm"
          >
            {accessStatus === 'loading'
              ? 'Checking page access…'
              : 'Page access could not be checked.'}
          </p>
        ) : null}
        {accessStatus === 'ready' && shownSections.length === 0 ? (
          <p
            data-testid={`${prefix}nav-empty`}
            className="text-sidebar-foreground/70 px-3 py-2 text-sm"
          >
            Every page is hidden. Customize pages to show one.
          </p>
        ) : null}
        {shownSections.map((section, sectionIndex) => {
          const slug = slugifyGroup(section.group)

          if (customizing) {
            // Every group lists open here: a switch the reader cannot see is
            // a page they cannot bring back.
            return (
              <div
                key={section.group}
                data-testid={`${prefix}nav-section-container-${slug}`}
                className="flex flex-col gap-1"
              >
                <div className="mt-2 flex items-center gap-1">
                  <SectionCheckbox
                    section={section}
                    hiddenPages={hiddenPages}
                    testId={`${prefix}nav-section-${slug}`}
                    onToggle={() => onToggleSection(section)}
                  />
                  {section.group !== OPERATIONS_GROUP ? (
                    <span className="flex shrink-0 items-center">
                      {([-1, 1] as const).map(direction => (
                        <button
                          key={direction}
                          type="button"
                          data-testid={`${prefix}nav-move-${slug}-${direction === -1 ? 'up' : 'down'}`}
                          aria-label={`Move ${section.group} ${direction === -1 ? 'up' : 'down'}`}
                          disabled={
                            direction === -1
                              ? sectionIndex === 0
                              : sectionIndex === shownSections.length - 1 ||
                                shownSections[sectionIndex + 1]?.group ===
                                  OPERATIONS_GROUP
                          }
                          className={cn(
                            rowClass(false),
                            'p-2 disabled:opacity-40'
                          )}
                          onClick={event => {
                            const button = event.currentTarget
                            onMoveSection(section.group, direction)
                            setMoveNotice(
                              `${section.group}, position ${sectionIndex + direction + 1} of ${sections.length - (sections.some(item => item.group === OPERATIONS_GROUP) ? 1 : 0)}`
                            )
                            requestAnimationFrame(() => {
                              if (button.disabled) {
                                button.parentElement
                                  ?.querySelector<HTMLButtonElement>(
                                    'button:not(:disabled)'
                                  )
                                  ?.focus()
                              } else {
                                button.focus()
                              }
                            })
                          }}
                        >
                          <Icon
                            name={
                              direction === -1 ? 'chevron-up' : 'chevron-down'
                            }
                            className="size-4"
                          />
                        </button>
                      ))}
                    </span>
                  ) : null}
                </div>
                {section.pages.map(page => (
                  <PageSwitch
                    key={page.id}
                    page={page}
                    shown={!hiddenPages.includes(page.id)}
                    testId={`${prefix}nav-switch-${page.id}`}
                    onToggle={() => onTogglePage(page.id)}
                  />
                ))}
              </div>
            )
          }

          // Every section opens by default. A reader who has never touched the
          // rail should still see every destination it holds; collapsing is
          // something they choose, not something the rail starts them with.
          const open = !collapsedGroups.includes(section.group)
          // A closed group hides the row carrying `aria-current`, so the header
          // answers for it. Only while closed: an open group already shows that
          // row, and a second marker would name the same page twice.
          const holdsCurrent =
            !open &&
            section.pages.some(page => normalizePath(page.path) === activePath)
          const whole = declared.get(section.group) ?? section
          const { shown, total } = sectionVisibility(whole, hiddenPages)
          const hiddenCount = total - shown
          const labelParts = [
            section.group,
            holdsCurrent ? 'holds the current page' : null,
            hiddenCount > 0 ? `${hiddenCount} hidden` : null
          ].filter(part => part !== null)

          return (
            <div
              key={section.group}
              data-testid={`${prefix}nav-section-container-${slug}`}
              className="flex flex-col gap-1"
            >
              <button
                type="button"
                data-testid={`${prefix}nav-group-${slug}`}
                aria-expanded={open}
                aria-controls={`${prefix}nav-group-${slug}-items`}
                // A visually hidden span beside the dot is the obvious spelling,
                // but the accessible name trims each node and joins them without
                // a separator: "BlytzCollectholds the current page".
                aria-label={
                  labelParts.length > 1 ? labelParts.join(', ') : undefined
                }
                className={cn(rowClass(holdsCurrent), 'justify-between')}
                onClick={() => onToggleGroup(section.group)}
              >
                <span className="flex items-center gap-2">
                  {section.group}
                  {holdsCurrent ? (
                    <span
                      aria-hidden="true"
                      className="bg-sidebar-accent-foreground size-1.5 shrink-0 rounded-full"
                    />
                  ) : null}
                </span>
                <span className="flex items-center gap-2">
                  {hiddenCount > 0 ? (
                    <span
                      data-testid={`${prefix}nav-group-${slug}-hidden`}
                      className="text-sidebar-foreground/60 text-xs font-normal"
                    >
                      {hiddenCount} hidden
                    </span>
                  ) : null}
                  <Icon
                    name={open ? 'chevron-up' : 'chevron-down'}
                    className="size-4 shrink-0"
                  />
                </span>
              </button>
              {open ? (
                <div
                  id={`${prefix}nav-group-${slug}-items`}
                  data-testid={`${prefix}nav-group-${slug}-items`}
                  className="flex flex-col gap-1 pl-4"
                >
                  {section.pages.map(page => (
                    <NavRow
                      key={page.id}
                      page={page}
                      active={activePath === normalizePath(page.path)}
                      testId={`${prefix}nav-${page.id}`}
                      onNavigate={() => navigate(page.path)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
        {customizing &&
        sections.some(
          section =>
            sectionVisibility(section, hiddenPages).shown < section.pages.length
        ) ? (
          <button
            type="button"
            data-testid={`${prefix}nav-show-all`}
            className={cn(rowClass(false), 'mt-2 w-full')}
            onClick={onShowAllPages}
          >
            <Icon name="eye" className="size-4 shrink-0" />
            Show every page
          </button>
        ) : null}
      </div>
    </nav>
  )
}

/**
 * The rail's pinned foot: the pinned pages, then one account row, which opens
 * the zone, the theme and sign out.
 *
 * Outside the scrolling page list, because that list grows with every app this
 * console gains. A pinned page is a tool, not a report, so it stays in reach
 * however long the list grows.
 *
 * @param props - Footer props.
 * @param props.mobile - True inside the sheet, false on the desktop rail.
 * @param props.onSelect - Closes the mobile sheet before a navigation or a sign-out runs.
 * @param props.pathname - The current path, to mark the active pinned page.
 * @param props.pages - The pinned pages this viewer may open.
 * @returns The rendered footer.
 */
export function SidebarFooter({
  mobile = false,
  onSelect,
  pathname,
  pages
}: {
  mobile?: boolean
  onSelect: () => void
  pathname: string
  pages: readonly InternalPage[]
}): JSX.Element {
  const router = useRouter()
  const prefix = mobile ? 'mobile-' : ''
  const activePath = normalizePath(pathname)

  return (
    <div className="shrink-0 p-3 pt-0">
      <div className="bg-sidebar-border my-2 h-px" />
      {pages.length > 0 ? (
        <nav
          data-testid={`${prefix}nav-pinned`}
          aria-label="Tools"
          className="mb-1 flex flex-col gap-1"
        >
          {pages.map(page => (
            <NavRow
              key={page.id}
              page={page}
              icon={page.icon}
              active={activePath === normalizePath(page.path)}
              testId={`${prefix}nav-${page.id}`}
              onNavigate={() => {
                onSelect()
                router.history.push(page.path)
              }}
            />
          ))}
        </nav>
      ) : null}
      <AccountMenu
        data-testid={mobile ? 'mobile-session' : 'session'}
        onSelect={onSelect}
      />
    </div>
  )
}
